import React, { useState } from "react";
import { Phone, Mail, MapPin, Clock, MessageCircle, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import api from "@/lib/api";
import { useSettings, whatsappHref } from "@/lib/settings";

export default function ContactPage() {
  const { settings } = useSettings();
  const [form, setForm] = useState({ name: "", phone: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.message) { toast.error("Ad ve mesaj alanları zorunludur"); return; }
    setLoading(true);
    try {
      await api.post("/contact", form);
      toast.success("Mesajınız iletildi. En kısa sürede dönüş yapacağız.");
      setForm({ name: "", phone: "", email: "", subject: "", message: "" });
    } catch {
      toast.error("Mesaj gönderilemedi.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12" data-testid="contact-page">
      <div className="mb-10">
        <div className="text-xs font-bold uppercase tracking-[0.25em] text-brand-600 mb-3">İletişim</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tight text-ink-900">Bize <span className="gradient-text">Ulaşın</span></h1>
        <p className="mt-3 text-ink-500 max-w-2xl">En hızlı iletişim için WhatsApp Business hattımızı kullanabilir veya aşağıdaki formu doldurabilirsiniz.</p>
      </div>

      <div className="mb-10">
        <a
          data-testid="contact-whatsapp-hero"
          href={whatsappHref(settings.whatsapp_number, "Merhaba, mobilya için bilgi almak istiyorum.")}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-3 rounded-full bg-[#25D366] text-white px-7 py-4 text-sm font-bold uppercase tracking-widest shadow-lg hover:brightness-110 transition-transform hover:-translate-y-0.5"
        >
          <MessageCircle className="h-5 w-5" /> WhatsApp'tan Anında Yaz
        </a>
      </div>

      <div className="grid lg:grid-cols-3 gap-6 mb-10">
        {[
          [Phone, "Telefon", settings.phone || "+90 533 508 93 24"],
          [Mail, "E-posta", settings.email || "info@malatyamobilyatasarim.com"],
          [MapPin, "Adres", settings.address || "Malatya, Türkiye"],
          [Clock, "Çalışma Saatleri", settings.working_hours || "Pzt - Cmt: 09:00 - 19:00"],
        ].map(([Icon, label, value]) => (
          <div key={label} className="rounded-3xl border border-ink-100 bg-white p-6 flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl gradient-primary-soft grid place-items-center text-brand-600 shrink-0"><Icon className="h-6 w-6" /></div>
            <div>
              <div className="text-xs font-bold uppercase tracking-widest text-ink-500">{label}</div>
              <div className="mt-1 font-semibold text-ink-900">{value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-8">
        <form onSubmit={submit} className="rounded-3xl border border-ink-100 bg-white p-7 space-y-5" data-testid="contact-form">
          <h2 className="font-display font-bold text-2xl">Mesaj Bırakın</h2>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">Ad Soyad *</Label>
              <Input data-testid="contact-name" required value={form.name} onChange={(e) => update("name", e.target.value)} className="mt-1.5 rounded-xl border-ink-100 focus-visible:ring-brand-500" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">Telefon</Label>
              <Input data-testid="contact-phone" value={form.phone} onChange={(e) => update("phone", e.target.value)} className="mt-1.5 rounded-xl border-ink-100 focus-visible:ring-brand-500" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">E-posta</Label>
              <Input data-testid="contact-email" type="email" value={form.email} onChange={(e) => update("email", e.target.value)} className="mt-1.5 rounded-xl border-ink-100 focus-visible:ring-brand-500" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">Konu</Label>
              <Input data-testid="contact-subject" value={form.subject} onChange={(e) => update("subject", e.target.value)} className="mt-1.5 rounded-xl border-ink-100 focus-visible:ring-brand-500" />
            </div>
            <div className="sm:col-span-2">
              <Label className="text-xs uppercase tracking-widest text-ink-500 font-bold">Mesaj *</Label>
              <Textarea data-testid="contact-message" required rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} className="mt-1.5 rounded-xl border-ink-100 focus-visible:ring-brand-500" />
            </div>
          </div>
          <Button data-testid="contact-submit" disabled={loading} type="submit" className="gradient-primary text-white rounded-full h-12 px-8 font-bold uppercase text-sm tracking-wider">
            <Send className="h-4 w-4 mr-2" />
            {loading ? "Gönderiliyor..." : "Mesaj Gönder"}
          </Button>
        </form>

        <div className="rounded-3xl overflow-hidden border border-ink-100 bg-white min-h-[400px]">
          <iframe
            title="Konum"
            src={settings.map_embed_url || "https://www.google.com/maps?q=Malatya&output=embed"}
            className="w-full h-full min-h-[400px] border-0"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      </div>
    </div>
  );
}
